import metadataIcon from '../../assets/metric-metadata-icon-blue.svg';
import { useEffect, useRef, useState } from "react";
import MetricMetadataModal from "../MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata } from "../../utils/dashboardUtils";

interface SingleBarProps {
    metric: DisaggregatedMetric;
    width: string;
    disaggData: DisaggregatedMetric[] | null;
    name: string;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: string;
    showMetadataIcon?: boolean;
};

const SingleBar = ({ metric, disaggData, name, geoDetails, geoTag, selectionActive, svg, showMetadataIcon = true }: SingleBarProps) => {
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Destructure metric properties
    const { id, versions } = metric;
    const { value } = versions[0];

    // Refs for metadata modal
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const modalId = `metric-metadata-modal-${id ?? 'main'}`;
    const clickDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchCache = useRef<Record<string, Promise<any> | undefined>>({});

    function fetchWithCache<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
        if (fetchCache.current[key]) {
            // Return ongoing request if already in-flight
            return fetchCache.current[key] as Promise<T>;
        }

        const promise = fetchFn().finally(() => {
            delete fetchCache.current[key];
        });

        fetchCache.current[key] = promise;
        return promise;
    }

    // Prefetch on mount (throttled sequentially)
    useEffect(() => {
        // Only fetch if not already cached or loaded
        if (!metadata) {
            fetchWithCache(`metadata-${id}`, () => fetchMetricMetadata(id))
            .then(res => res?.data && setMetadata(res.data))
            .catch(console.error);
        }

        if (!distributionData) {
            const distKey = `distData-${id}-${geoDetails.map(d => d.lod).join(',')}-${geoTag?.type}-${geoTag?.value}`;
            fetchWithCache(distKey, () => fetchIndicatorBinCount(id, geoDetails, geoTag))
            .then(res => res && setDistributionData(res))
            .catch(console.error);
        }

        if (!countyDistributionData) {
            const countyKey = `countyDist-${id}-53033`;
            fetchWithCache(countyKey, () => fetchDistributionData(id, [], null))
            .then(res => res && setCountyDistributionData(res))
            .catch(console.error);
        }
    }, [id, geoDetails, geoTag]);

    const handleClick = async () => {
        if (clickDebounceRef.current) clearTimeout(clickDebounceRef.current);

        clickDebounceRef.current = setTimeout(async () => {
            setShowMetadata((prev) => !prev);
            setLoading(true);

            try {
            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                metadata
                ? Promise.resolve({ data: metadata })
                : fetchWithCache(`metadata-${id}`, () => fetchMetricMetadata(id)),

                distributionData
                ? Promise.resolve(distributionData)
                : fetchWithCache(`distData-${id}-${geoDetails.map(d => d.lod).join(',')}-${geoTag?.type}-${geoTag?.value}`, () =>
                    fetchIndicatorBinCount(id, geoDetails, geoTag)
                    ),

                countyDistributionData
                ? Promise.resolve(countyDistributionData)
                : fetchWithCache(`countyDist-${id}-53033`, () =>
                    fetchDistributionData(id, [], null)
                    ),
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

            } catch (error) {
            const errorMessage = (error as Error).message || 'An unexpected error occurred.';
            console.error('Error fetching data:', error);
            setError(new Error(`Failed to load data: ${errorMessage}`));
            } finally {
            setLoading(false);
            }
        }, 150);
    };

    return (
        <div className="mx-2 my-2">
            <div className="flex items-center justify-start w-full">
                <p className={`text-port-text text-[11px] font-bold mr-2 w-12`}>{name}</p>
                {/* Dynamic width bar for formatted value */}
                <div 
                    className="bg-[#4F92B1] text-white text-center text-xs font-bold py-1 h-3"
                    style={{ width: `${value * 2}px`, maxWidth: '300px' }}
                >
                </div>

                {showMetadataIcon && 
                    <button
                        type="button"
                        onClick={handleClick}
                        aria-haspopup="dialog"
                        aria-expanded={showMetadata ? 'true' : 'false'}
                        aria-controls={modalId}
                        aria-label={`Additional information about ${name}`}
                        className="
                        inline-flex h-6 w-6 items-center justify-center rounded
                        transition-transform hover:scale-105 focus:scale-105
                        focus-visible:outline-none
                        focus-visible:ring-2 focus-visible:ring-offset-2
                        focus-visible:ring-neutral-900 focus-visible:ring-offset-white
                        dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900
                        "
                    >
                        <img src={metadataIcon} alt="" aria-hidden="true" className="h-3 w-3" draggable="false" />
                    </button>
                }
            </div>
            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={id}
                metadata={metadata}
                stateData={null}
                nationalData={null}
                disaggData={disaggData}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                svg={svg}
                loading={loading}
                error={error}
                triggerRef={triggerRef}
                modalId={modalId}
            />
        </div>
    );
};


export default SingleBar;
